import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faLocationCrosshairs, faMagnifyingGlass, faMapLocationDot, faRoute, faListUl, faCircleCheck, faCircleInfo, faXmark } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../../components/SearchableSelect';
import ConfirmationModal from './ConfirmationModal';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

// Max stops in a Google Maps directions URL (origin + up to ~9 waypoints + destination).
const MAX_TOUR_STOPS = 10;

// Inline SVGs (FontAwesome paths) so popup HTML matches the "Gérer les adresses" icons.
const SVG_CHECK = '<svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/></svg>';
const SVG_UNDO = '<svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M125.7 160H176c17.7 0 32 14.3 32 32s-14.3 32-32 32H48c-17.7 0-32-14.3-32-32V64c0-17.7 14.3-32 32-32s32 14.3 32 32v51.2L97.6 97.6c87.5-87.5 229.3-87.5 316.8 0s87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3s-163.8-62.5-226.3 0L125.7 160z"/></svg>';
const SVG_TRASH = '<svg width="14" height="14" viewBox="0 0 448 512" fill="currentColor"><path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"/></svg>';
const ICON_BTN = (color, bg, border) => `display:inline-flex;align-items:center;justify-content:center;width:34px;height:32px;border:1px solid ${border};border-radius:4px;background:${bg};color:${color};cursor:pointer;flex:0 0 auto`;

const PALETTE = ['#dc2626', '#2563eb', '#059669', '#d97706', '#7c3aed', '#db2777', '#0891b2', '#65a30d', '#ea580c', '#4f46e5'];
const PENDING_LABEL = { create: 'Ajout en attente', update: 'Modification en attente', delete: 'Suppression en attente' };

let INSTANCE_SEQ = 0;

/**
 * Reusable Leaflet map (window.L) of quota location addresses.
 *
 * Props:
 *  - endpoint: API URL returning { data: [addresses] }
 *  - allowVisitedToggle: show the "mark visited" button in popups (supervisor's own map)
 *  - showSupervisor: show supervisor name in popup + a supervisor filter (staff map)
 *  - embedded: render only the filter bar + map (no page header), for use inside a page
 *  - title / subtitle: header text when not embedded
 *  - heightStyle: CSS height for the map container
 */
const AddressesMapView = ({
  endpoint,
  allowVisitedToggle = false,
  visitedBase = '/quotas/my-addresses', // POST `${visitedBase}/${id}/visited`
  allowDelete = false, // CT/CTS: DELETE /quotas/locations/{id} from a popup (not if visited)
  showSupervisor = false,
  embedded = false,
  title = 'Carte des adresses',
  subtitle = '',
  heightStyle = '65vh',
  refreshKey = 0, // bump to force a re-fetch (e.g. after an address import/delete)
  onChanged = null, // called after an in-map delete so the parent can refresh (e.g. pending count)
  // Address notes are internal to CT/CTS. The supervisor map passes neither flag, and the
  // API does not even send `notes` to prestataires.
  showNotes = false,
  canEditNotes = false,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, pendingId } for the confirm modal
  const [notesTarget, setNotesTarget] = useState(null); // { id, name, notes } for the notes modal
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState('');
  // Editable content: only Escape and the explicit buttons dismiss it.
  const notesDismiss = useModalDismiss(() => setNotesTarget(null), { backdropClose: false, enabled: !!notesTarget });
  const [localRefresh, setLocalRefresh] = useState(0);
  const [tourConfirm, setTourConfirm] = useState(null); // computed order awaiting confirmation (over the cap)
  const [error, setError] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supervisorFilter, setSupervisorFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [locating, setLocating] = useState(false);

  const [userPos, setUserPos] = useState(null); // { lat, lng }
  const [showList, setShowList] = useState(false);

  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const markerByIdRef = useRef({});
  const userMarkerRef = useRef(null);
  const containerIdRef = useRef(`addr-map-${++INSTANCE_SEQ}`);
  const toggleFnNameRef = useRef(`qaddrToggle_${INSTANCE_SEQ}`);
  const deleteFnNameRef = useRef(`qaddrDelete_${INSTANCE_SEQ}`);
  const notesFnNameRef = useRef(`qaddrNotes_${INSTANCE_SEQ}`);

  const distMeters = (aLat, aLng, bLat, bLng) => {
    try { return window.L.latLng(aLat, aLng).distanceTo(window.L.latLng(bLat, bLng)); }
    catch (e) { return Infinity; }
  };

  const surveys = useMemo(() => {
    const m = new Map();
    addresses.forEach(a => {
      if (a.survey_id != null && !m.has(a.survey_id)) m.set(a.survey_id, a.survey_title || `Enquête ${a.survey_id}`);
    });
    return Array.from(m.entries()).map(([id, t]) => ({ id, title: t }));
  }, [addresses]);

  const surveyColor = useMemo(() => {
    const m = {};
    surveys.forEach((s, i) => { m[s.id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [surveys]);

  const supervisorsList = useMemo(() => {
    if (!showSupervisor) return [];
    const m = new Map();
    addresses.forEach(a => {
      if (a.supervisor && !m.has(a.supervisor.id)) m.set(a.supervisor.id, a.supervisor.name);
    });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [addresses, showSupervisor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return addresses.filter(a => {
      if (surveyFilter !== 'all' && String(a.survey_id) !== String(surveyFilter)) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (showSupervisor && supervisorFilter !== 'all' && String(a.supervisor?.id) !== String(supervisorFilter)) return false;
      if (q) {
        const hay = `${a.name || ''} ${a.agency_code || ''} ${a.address_text || ''} ${a.survey_title || ''} ${a.supervisor?.name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [addresses, surveyFilter, statusFilter, supervisorFilter, search, showSupervisor]);

  // Not-yet-visited locations sorted by straight-line distance from the user's position.
  const distanceSorted = useMemo(() => {
    if (!userPos) return [];
    return filtered
      .filter(a => a.status !== 'visited')
      .map(a => ({ ...a, _dist: distMeters(userPos.lat, userPos.lng, a.latitude, a.longitude) }))
      .sort((x, y) => x._dist - y._dist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, userPos]);

  const pendingCount = useMemo(() => filtered.filter(a => a.status !== 'visited').length, [filtered]);

  // Nearest-neighbour visiting order (pending only) from the user's position.
  const nnOrderPending = () => {
    if (!userPos) return [];
    const remaining = filtered.filter(a => a.status !== 'visited');
    const order = [];
    let cur = { lat: userPos.lat, lng: userPos.lng };
    while (remaining.length && order.length < MAX_TOUR_STOPS) {
      let bi = 0, bd = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const d = distMeters(cur.lat, cur.lng, remaining[i].latitude, remaining[i].longitude);
        if (d < bd) { bd = d; bi = i; }
      }
      const next = remaining.splice(bi, 1)[0];
      order.push(next);
      cur = { lat: next.latitude, lng: next.longitude };
    }
    return order;
  };

  const openTourUrl = (order) => {
    if (!userPos || !order || order.length === 0) return;
    const origin = `${userPos.lat},${userPos.lng}`;
    const destination = `${order[order.length - 1].latitude},${order[order.length - 1].longitude}`;
    const waypoints = order.slice(0, -1).map(a => `${a.latitude},${a.longitude}`).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const planTour = () => {
    const order = nnOrderPending();
    if (!userPos || order.length === 0) return;
    // Over the Google Maps cap → confirm first, then open. Otherwise open straight away.
    if (pendingCount > MAX_TOUR_STOPS) {
      setTourConfirm(order);
    } else {
      openTourUrl(order);
    }
  };

  const focusAddress = (a) => {
    if (!mapRef.current) return;
    const m = markerByIdRef.current[a.id];
    // With clustering, reveal the marker from its cluster before opening the popup.
    if (m && markersLayerRef.current && typeof markersLayerRef.current.zoomToShowLayer === 'function') {
      markersLayerRef.current.zoomToShowLayer(m, () => m.openPopup());
    } else {
      mapRef.current.setView([a.latitude, a.longitude], 15);
      if (m) m.openPopup();
    }
  };

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(endpoint);
        if (active) setAddresses(res.data.data || []);
      } catch (err) {
        if (active) setError('Erreur lors du chargement des adresses');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, refreshKey, localRefresh]);

  // Per-instance global for the popup "mark visited" button.
  useEffect(() => {
    if (!allowVisitedToggle) return undefined;
    const fnName = toggleFnNameRef.current;
    window[fnName] = async (id) => {
      try {
        const res = await axios.post(`${visitedBase}/${id}/visited`);
        setAddresses(prev => prev.map(a => a.id === id ? { ...a, status: res.data.status } : a));
      } catch (err) { /* ignore */ }
    };
    return () => { delete window[fnName]; };
  }, [allowVisitedToggle, visitedBase]);

  // Per-instance global for the popup delete button (CT/CTS) — opens a confirm modal.
  useEffect(() => {
    if (!allowDelete) return undefined;
    const fnName = deleteFnNameRef.current;
    window[fnName] = (id, pendingId) => {
      if (mapRef.current) mapRef.current.closePopup();
      setDeleteTarget({ id, pendingId: pendingId || null });
    };
    return () => { delete window[fnName]; };
  }, [allowDelete]);

  // Per-instance global for the popup info button — opens the notes modal.
  useEffect(() => {
    if (!showNotes) return undefined;
    const fnName = notesFnNameRef.current;
    window[fnName] = (id) => {
      const a = addresses.find(x => String(x.id) === String(id));
      if (!a) return;
      if (mapRef.current) mapRef.current.closePopup();
      setNotesTarget({ id: a.id, name: a.name, notes: a.notes || '' });
      setNotesDraft(a.notes || '');
      setNotesError('');
    };
    return () => { delete window[fnName]; };
  }, [showNotes, addresses]);

  const saveNotes = async () => {
    if (!notesTarget) return;
    setNotesSaving(true);
    setNotesError('');
    try {
      const res = await axios.patch(`/quotas/locations/${notesTarget.id}/notes`, { notes: notesDraft });
      const saved = res.data?.notes ?? null;
      setAddresses(prev => prev.map(a => a.id === notesTarget.id ? { ...a, notes: saved } : a));
      setNotesTarget(null);
    } catch (err) {
      setNotesError(err.response?.data?.message || "Erreur lors de l'enregistrement des notes");
    } finally {
      setNotesSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id, pendingId } = deleteTarget;
    try {
      // A pending change is cancelled (removes the pending row); a committed address is
      // deleted (which itself stages a pending-delete for a chef_terrain).
      if (pendingId) {
        await axios.delete(`/quotas/pending-locations/${pendingId}`);
      } else {
        await axios.delete(`/quotas/locations/${id}`);
      }
      setDeleteTarget(null);
      setLocalRefresh(k => k + 1);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression.');
      setDeleteTarget(null);
    }
  };

  const buildIcon = (a) => {
    const color = a.pending_operation ? '#eab308' : (a.status === 'visited' ? '#16a34a' : (surveyColor[a.survey_id] || '#dc2626'));
    const check = a.pending_operation ? '' : (a.status === 'visited' ? '✓' : '');
    return window.L.divIcon({
      className: 'quota-addr-pin',
      html: `<div style="background:${color};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;">
               <span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:bold;">${check}</span></div>`,
      iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -20],
    });
  };

  const buildPopup = (a) => {
    const directions = `https://www.google.com/maps/dir/?api=1&destination=${a.latitude},${a.longitude}`;
    const esc = (s) => (s ? String(s).replace(/</g, '&lt;').replace(/>/g, '&gt;') : '');
    const directionsLink = `<a href="${directions}" target="_blank" rel="noopener noreferrer" style="flex:1;text-align:center;padding:6px 8px;font-size:12px;border-radius:4px;background:#2563eb;color:#fff;text-decoration:none">Itinéraire</a>`;

    const isPending = !!a.pending_operation;
    const pendingArg = a.pending_id != null ? a.pending_id : 'null';
    const deleteIconBtn = `<button onclick="window.${deleteFnNameRef.current}('${a.id}', ${pendingArg})" title="${isPending ? 'Annuler cette modification en attente' : 'Supprimer'}" style="${ICON_BTN('#dc2626', '#fef2f2', '#fecaca')}">${SVG_TRASH}</button>`;

    // Info button: staff only. Filled when the address carries a note, hollow otherwise.
    const hasNote = !!(a.notes && String(a.notes).trim() !== '');
    // Same glyph as the modal header: FontAwesome's circle-info path inlined, because a
    // Leaflet popup is an HTML string and can't host a React <FontAwesomeIcon>.
    const infoPath = Array.isArray(faCircleInfo.icon[4]) ? faCircleInfo.icon[4][0] : faCircleInfo.icon[4];
    const infoSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${faCircleInfo.icon[0]} ${faCircleInfo.icon[1]}" width="13" height="13" fill="currentColor" style="display:block"><path d="${infoPath}"/></svg>`;
    const notesIconBtn = showNotes
      ? `<button onclick="window.${notesFnNameRef.current}('${a.id}')" title="${hasNote ? 'Voir les notes' : 'Ajouter des notes'}" style="padding:6px 8px;border:1px solid ${hasNote ? '#2563eb' : '#d1d5db'};background:${hasNote ? '#eff6ff' : '#fff'};color:${hasNote ? '#1d4ed8' : '#6b7280'};border-radius:6px;cursor:pointer;line-height:1">${infoSvg}</button>`
      : '';

    let actions;
    if (isPending) {
      // Unapproved change: CT/CTS can cancel it; everyone gets directions.
      actions = `${allowDelete ? deleteIconBtn : ''}${directionsLink}`;
    } else if (allowDelete) {
      // Staff (CT/CTS): compact icon buttons matching the "Gérer les adresses" modal.
      const visitedIconBtn = allowVisitedToggle
        ? `<button onclick="window.${toggleFnNameRef.current}(${a.id})" title="${a.status === 'visited' ? 'Marquer non visité' : 'Marquer visité'}" style="${ICON_BTN(a.status === 'visited' ? '#b45309' : '#059669', '#fff', '#d1d5db')}">${a.status === 'visited' ? SVG_UNDO : SVG_CHECK}</button>`
        : '';
      // CT/CTS don't need directions — just the manage actions.
      actions = `${notesIconBtn}${visitedIconBtn}${a.status !== 'visited' ? deleteIconBtn : ''}`;
    } else {
      // Supervisor: prominent labelled visited button.
      const visitedBtn = allowVisitedToggle
        ? `<button onclick="window.${toggleFnNameRef.current}(${a.id})" style="flex:1;padding:5px 8px;font-size:12px;border:0;border-radius:4px;background:#dc2626;color:#fff;cursor:pointer">${a.status === 'visited' ? 'Marquer non visité' : 'Marquer visité'}</button>`
        : '';
      actions = `${visitedBtn}${directionsLink}`;
    }

    const supLine = (showSupervisor && a.supervisor)
      ? `<div style="font-size:12px;color:#374151">Prestataire : <strong>${esc(a.supervisor.name)}</strong>${a.supervisor.ref ? ` (${esc(a.supervisor.ref)})` : ''}</div>`
      : '';
    return `
      <div style="min-width:200px">
        <div style="font-weight:600;margin-bottom:2px">${esc(a.name)}${a.agency_code ? ` <span style="color:#6b7280">(${esc(a.agency_code)})</span>` : ''}</div>
        ${a.address_text ? `<div style="color:#374151;font-size:12px;margin-bottom:4px">${esc(a.address_text)}</div>` : ''}
        <div style="font-size:12px;color:#6b7280">${esc(a.survey_title || '')}${a.wave_label ? ` · ${esc(a.wave_label)}` : ''}</div>
        ${supLine}
        ${isPending
          ? `<div style="font-size:12px;margin:4px 0"><strong style="color:#a16207">${PENDING_LABEL[a.pending_operation] || 'En attente de validation'}</strong></div>`
          : `<div style="font-size:12px;margin:4px 0">Statut : <strong style="color:${a.status === 'visited' ? '#059669' : '#b45309'}">${a.status === 'visited' ? 'Visité' : 'À visiter'}</strong></div>`}
        <div style="display:flex;gap:6px;margin-top:6px;align-items:stretch">
          ${actions}
        </div>
      </div>`;
  };

  const drawMarkers = () => {
    if (!mapRef.current || !markersLayerRef.current) return;
    markersLayerRef.current.clearLayers();
    markerByIdRef.current = {};
    const bounds = [];
    filtered.forEach(a => {
      const marker = window.L.marker([a.latitude, a.longitude], { icon: buildIcon(a) });
      marker.bindPopup(buildPopup(a));
      markersLayerRef.current.addLayer(marker);
      markerByIdRef.current[a.id] = marker;
      bounds.push([a.latitude, a.longitude]);
    });
    if (bounds.length > 0) {
      try { mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 }); } catch (e) { /* noop */ }
    }
  };

  useEffect(() => {
    if (loading) return undefined;
    const init = () => {
      const container = document.getElementById(containerIdRef.current);
      if (!container) { setTimeout(init, 100); return; }
      if (mapRef.current) return;
      if (typeof window === 'undefined' || !window.L) { setTimeout(init, 100); return; }
      const map = window.L.map(containerIdRef.current).setView([31.7917, -7.0926], 6);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      markersLayerRef.current = (window.L.markerClusterGroup
        ? window.L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 45, spiderfyOnMaxZoom: true })
        : window.L.layerGroup()).addTo(map);
      mapRef.current = map;
      drawMarkers();
    };
    const t = setTimeout(init, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Cleanup the Leaflet map on unmount (important for embedded/toggled use).
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    drawMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  // Leaflet needs a size recalculation when the list panel shows/hides.
  useEffect(() => {
    if (mapRef.current) {
      const t = setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [showList]);

  const handleLocate = () => {
    if (!navigator.geolocation || !mapRef.current) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (userMarkerRef.current) mapRef.current.removeLayer(userMarkerRef.current);
        const group = window.L.layerGroup();
        window.L.circleMarker([latitude, longitude], { radius: 8, color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.9, weight: 2 }).bindPopup('Ma position').addTo(group);
        window.L.circle([latitude, longitude], { radius: accuracy || 50, color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.1, weight: 1 }).addTo(group);
        group.addTo(mapRef.current);
        userMarkerRef.current = group;
        mapRef.current.setView([latitude, longitude], 13);
        setUserPos({ lat: latitude, lng: longitude });
        setShowList(true);
        setLocating(false);
      },
      () => { setLocating(false); setError('Impossible d\'obtenir votre position (autorisation refusée ?).'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const filterBar = (
    <div className="relative z-[1100] bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-3 flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[180px]">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, code, adresse...)"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
      </div>
      {surveys.length > 0 && (
        <div className="w-56 text-sm">
          <SearchableSelect
            options={surveys}
            selectedValue={surveyFilter === 'all' ? null : surveyFilter}
            onChange={(id) => setSurveyFilter(id == null ? 'all' : id)}
            getOptionLabel={(o) => o.title}
            showOptionMeta={false}
            placeholder="Toutes les enquêtes"
            searchPlaceholder="Rechercher une enquête..."
            triggerClassName="px-3 py-2"
          />
        </div>
      )}
      {showSupervisor && supervisorsList.length > 1 && (
        <div className="w-56 text-sm">
          <SearchableSelect
            options={supervisorsList}
            selectedValue={supervisorFilter === 'all' ? null : supervisorFilter}
            onChange={(id) => setSupervisorFilter(id == null ? 'all' : id)}
            getOptionLabel={(o) => o.name}
            showOptionMeta={false}
            placeholder="Tous les prestataires"
            searchPlaceholder="Rechercher un prestataire..."
            triggerClassName="px-3 py-2"
          />
        </div>
      )}
      <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
        className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 outline-none">
        <option value="all">Tous les statuts</option>
        <option value="pending">À visiter</option>
        <option value="visited">Visité</option>
      </select>
      {!embedded && (
        <button onClick={handleLocate} disabled={locating}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
          <FontAwesomeIcon icon={locating ? faSpinner : faLocationCrosshairs} className={locating ? 'animate-spin' : ''} />
          Ma position
        </button>
      )}
      {userPos && (
        <>
          <button onClick={() => setShowList(v => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            <FontAwesomeIcon icon={faListUl} />
            {showList ? 'Masquer la liste' : 'Liste par distance'}
          </button>
          <button onClick={planTour} disabled={pendingCount === 0}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            title={`Itinéraire optimisé (plus proche voisin) des ${Math.min(pendingCount, MAX_TOUR_STOPS)} emplacement(s) à visiter les plus proches, dans Google Maps`}>
            <FontAwesomeIcon icon={faRoute} />
            Planifier la tournée
          </button>
        </>
      )}
      <span className="text-sm text-gray-500 ml-auto">{filtered.length} emplacement(s)</span>
    </div>
  );

  const legend = surveys.length > 0 && (
    <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-600">
      {surveys.map(s => (
        <span key={s.id} className="inline-flex items-center gap-1.5">
          <span style={{ backgroundColor: surveyColor[s.id] }} className="w-3 h-3 rounded-full inline-block" />{s.title}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span style={{ backgroundColor: '#16a34a' }} className="w-3 h-3 rounded-full inline-block" />Visité
      </span>
      {filtered.some(a => a.pending_operation) && (
        <span className="inline-flex items-center gap-1.5">
          <span style={{ backgroundColor: '#eab308' }} className="w-3 h-3 rounded-full inline-block" />En attente
        </span>
      )}
    </div>
  );

  const body = (
    <>
      {filterBar}
      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      {legend}
      {loading ? (
        <div className="flex items-center justify-center bg-white rounded-lg border border-gray-200" style={{ height: heightStyle }}>
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-3xl" />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-3">
          <div id={containerIdRef.current} className="w-full lg:flex-1 rounded-lg border border-gray-200 shadow-sm" style={{ height: heightStyle, minWidth: 0 }} />
          {showList && userPos && (
            <div className="lg:w-80 w-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-y-auto" style={{ maxHeight: heightStyle }}>
              <div className="px-3 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700 sticky top-0 bg-white">
                Par distance ({distanceSorted.length})
              </div>
              <ul className="divide-y divide-gray-100">
                {distanceSorted.map(a => (
                  <li key={a.id}>
                    <button onClick={() => focusAddress(a)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {a.name}{a.agency_code ? ` (${a.agency_code})` : ''}
                        </span>
                        <span className="block text-xs text-gray-500 truncate">
                          {a.survey_title || ''}{showSupervisor && a.supervisor ? ` · ${a.supervisor.name}` : ''}
                        </span>
                      </span>
                      <span className="flex flex-col items-end flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-700">{(a._dist / 1000).toFixed(1)} km</span>
                        {a.status === 'visited' && <FontAwesomeIcon icon={faCircleCheck} className="text-green-500 text-xs mt-0.5" />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {!loading && addresses.length === 0 && (
        <p className="text-center text-gray-500 mt-4">Aucune adresse à afficher.</p>
      )}
    </>
  );

  const deleteModal = deleteTarget && (
    <ConfirmationModal
      show={!!deleteTarget}
      title={deleteTarget.pendingId ? 'Annuler la modification' : "Supprimer l'adresse"}
      message={deleteTarget.pendingId
        ? 'Annuler cette modification en attente ? Elle ne sera pas envoyée pour validation.'
        : 'Supprimer cette adresse ?'}
      confirmText={deleteTarget.pendingId ? 'Annuler la modif.' : 'Supprimer'}
      cancelText="Retour"
      variant="danger"
      onConfirm={confirmDelete}
      onCancel={() => setDeleteTarget(null)}
    />
  );


  // Staff-only notes dialog. Read-only unless the caller says the user may edit; like the
  // other modals here it is rendered outside the `isolate` so it sits above the map.
  const notesModal = notesTarget && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      {...notesDismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faCircleInfo} className="text-blue-600" />
              Notes de l'emplacement
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{notesTarget.name}</p>
          </div>
          <button onClick={() => setNotesTarget(null)} className="text-gray-400 hover:text-gray-600" title="Fermer">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="p-4">
          {canEditNotes ? (
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={6}
              maxLength={5000}
              placeholder="Horaires, accès, contact sur place..."
              className="w-full text-sm border border-gray-300 rounded-md p-2 outline-none focus:ring-2 focus:ring-red-500"
            />
          ) : (
            <div className="text-sm text-gray-700 whitespace-pre-wrap min-h-[3rem]">
              {notesTarget.notes || <span className="text-gray-400">Aucune note.</span>}
            </div>
          )}
          {notesError && <div className="mt-2 text-sm text-red-600">{notesError}</div>}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button onClick={() => setNotesTarget(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Fermer
          </button>
          {canEditNotes && (
            <button onClick={saveNotes} disabled={notesSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
              {notesSaving ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />Enregistrement...</> : 'Enregistrer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const tourModal = tourConfirm && (
    <ConfirmationModal
      show={!!tourConfirm}
      title="Tournée limitée à 10 arrêts"
      message={`Vous avez ${pendingCount} emplacement(s) à visiter, mais Google Maps limite l'itinéraire à ${MAX_TOUR_STOPS} arrêts. Seuls les ${MAX_TOUR_STOPS} plus proches seront inclus (${pendingCount - MAX_TOUR_STOPS} non inclus). Ouvrir l'itinéraire ?`}
      confirmText="Ouvrir l'itinéraire"
      cancelText="Annuler"
      variant="warning"
      onConfirm={() => { openTourUrl(tourConfirm); setTourConfirm(null); }}
      onCancel={() => setTourConfirm(null)}
    />
  );

  // `isolate` traps Leaflet's high z-indexes (panes/controls up to ~1000) in their own
  // stacking context so app modals (z-50) still render above the map. The confirm modal is
  // rendered OUTSIDE the isolate so it sits above the map.
  if (embedded) return <>{<div className="isolate">{body}</div>}{deleteModal}{tourModal}{notesModal}</>;

  return (
    <>
    <div className="isolate min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faMapLocationDot} className="text-red-600" />{title}
          </h1>
          {subtitle && <p className="text-gray-600 text-sm">{subtitle}</p>}
        </div>
        {body}
      </div>
    </div>
    {deleteModal}
    {tourModal}
    {notesModal}
    </>
  );
};

export default AddressesMapView;
